import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../Auth/AuthContext';
import LoginModal from '../Auth/LoginModal';
import RegisterModal from '../Auth/RegisterModal';
import LoadingOverlay from './LoadingOverlay';

const { FiMenu, FiX, FiUser, FiLogOut, FiLogIn, FiUserPlus, FiTarget, FiStar, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  const { 
    user, 
    signOut, 
    isPremium, 
    getRemainingFreeTrials, 
    displayName, 
    showLoginModal, 
    setShowLoginModal,
    signingOut
  } = useAuth();
  
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  
  const navigate = useNavigate();
  
  const remainingTrials = getRemainingFreeTrials();

  // Add event listener to allow other components to open the register modal
  useEffect(() => {
    const openRegisterModalHandler = () => setShowRegisterModal(true);
    document.addEventListener('openRegisterModal', openRegisterModalHandler);
    return () => document.removeEventListener('openRegisterModal', openRegisterModalHandler);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const closeUserDropdown = () => {
    setShowUserDropdown(false);
  };

  const handleSignOut = async () => {
    await signOut();
    closeMenu();
    closeUserDropdown();
  };

  return (
    <>
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center gap-2">
                <SafeIcon icon={FiTarget} className="text-2xl text-indigo-600" />
                <span className="text-xl font-bold text-gray-800">CreateMyMeal</span>
              </Link>
            </div>

            {/* User status indicator */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  {/* Premium Status Badge */}
                  {isPremium ? (
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-3 py-1 rounded-full flex items-center gap-1">
                      <SafeIcon icon={FiStar} className="text-sm" />
                      <span className="font-medium">Premium</span>
                    </div>
                  ) : (
                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {remainingTrials === Infinity ? 'Unlimited' : remainingTrials} trials left
                    </div>
                  )}

                  {/* User menu */}
                  <div className="relative ml-3">
                    <button
                      onClick={toggleUserDropdown}
                      className="flex items-center gap-2 bg-indigo-100 text-indigo-800 hover:bg-indigo-200 px-3 py-2 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiUser} className="text-lg" />
                      <span className="font-medium">{displayName}</span>
                      <SafeIcon icon={FiChevronDown} className={`text-sm transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* User dropdown menu */}
                    <AnimatePresence>
                      {showUserDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-10 border border-gray-200"
                        >
                          <Link
                            to="/profile"
                            onClick={closeUserDropdown}
                            className="block px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
                          >
                            Profile Settings
                          </Link>
                          <button
                            onClick={handleSignOut}
                            disabled={signingOut}
                            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {signingOut ? 'Signing out...' : 'Sign Out'}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1"
                  >
                    <SafeIcon icon={FiLogIn} className="text-sm" />
                    <span>Sign In</span>
                  </button>
                  <button
                    onClick={() => setShowRegisterModal(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                  >
                    <SafeIcon icon={FiUserPlus} className="text-sm" />
                    <span>Sign Up</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">{isMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu, show/hide based on menu state */}
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: isMenuOpen ? 'auto' : 0, opacity: isMenuOpen ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className="md:hidden overflow-hidden"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {user ? (
              <>
                {/* User info */}
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-gray-700">{displayName}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                  {isPremium ? (
                    <div className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-2 py-0.5 rounded-full text-xs mt-1">
                      <SafeIcon icon={FiStar} className="text-xs" />
                      <span>Premium</span>
                    </div>
                  ) : (
                    <div className="text-xs text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full inline-block mt-1">
                      {remainingTrials === Infinity ? 'Unlimited' : remainingTrials} trials left
                    </div>
                  )}
                </div>

                {/* Links */}
                <Link
                  to="/profile"
                  onClick={closeMenu}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Profile
                </Link>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                >
                  {signingOut ? 'Signing out...' : 'Sign Out'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    closeMenu();
                  }}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 w-full text-left"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setShowRegisterModal(true);
                    closeMenu();
                  }}
                  className="block px-3 py-2 rounded-md text-base font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 w-full text-left"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </motion.div>
      </header>

      {/* Auth Modals */}
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <RegisterModal isOpen={showRegisterModal} onClose={() => setShowRegisterModal(false)} />
      
      {/* Loading overlay during sign out */}
      {signingOut && <LoadingOverlay message="Signing out..." />}
    </>
  );
};

export default Header;